import { useEffect, useState, useCallback } from "react";
import { useAba } from "@/lib/useAba";
import { Plus, Pencil, Trash2, RefreshCw, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/common";
import { CompanyDialog } from "@/components/sistema/CompanyDialog";
import { PaymentMethodsTab } from "@/components/sistema/PaymentMethodsTab";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, imgUrl, maskDoc, maskPhone } from "@/lib/api";
import { toast } from "sonner";

export default function CompanyPage() {
  const aba = useAba("empresas");
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/stores").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (s) => {
    if (!window.confirm(`Excluir a empresa ${s.name}? Esta ação não pode ser desfeita.`)) return;
    try { await api.delete(`/stores/${s.id}`); toast.success("Empresa excluída"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const saved = async () => { setEdit(null); await load(); await refresh(); };
  return (
    <>
      <PageHeader eyebrow="Sistema" title="Minha empresa" subtitle="Dados da empresa principal e das filiais: logo, documentos, endereço, contatos e texto dos pedidos." actions={<>
        <Button variant="outline" onClick={load} data-testid="company-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />Atualizar</Button>
        <Button onClick={() => setEdit({})} data-testid="company-new-btn"><Plus className="h-4 w-4 mr-1" />Nova filial</Button>
      </>} />
      <Tabs key={aba} defaultValue={aba}>
        <TabsList><TabsTrigger value="empresas" data-testid="company-tab-empresas">Empresas</TabsTrigger><TabsTrigger value="pagamentos" data-testid="company-tab-pagamentos">Formas de pagamento</TabsTrigger></TabsList>
        <TabsContent value="pagamentos" className="mt-4"><PaymentMethodsTab /></TabsContent>
        <TabsContent value="empresas" className="mt-4 space-y-4">
      <div className="card-surface overflow-x-auto">
        <Table data-testid="company-table">
          <TableHeader><TableRow><TableHead className="w-16">Logo</TableHead><TableHead>Nome fantasia</TableHead><TableHead>Razão social</TableHead><TableHead>CPF / CNPJ</TableHead><TableHead>Cidade / UF</TableHead><TableHead>Telefone</TableHead><TableHead>Situação</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} className={s.active ? "" : "opacity-50"} data-testid={`company-row-${s.id}`}>
                <TableCell><div className="h-10 w-10 rounded-lg border bg-muted grid place-items-center overflow-hidden">{s.logo_url ? <img src={imgUrl(s.logo_url)} alt={`Logo ${s.name}`} className="h-full w-full object-cover" /> : <Building2 className="h-4 w-4 text-muted-foreground" />}</div></TableCell>
                <TableCell className="font-medium">{s.name} {s.is_main && <span className="ml-1 rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">Principal</span>}</TableCell>
                <TableCell>{s.razao_social || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{s.documento ? maskDoc(s.documento) : "—"}</TableCell>
                <TableCell>{s.endereco?.cidade ? `${s.endereco.cidade}/${s.endereco.uf}` : "—"}</TableCell>
                <TableCell>{s.phone ? maskPhone(s.phone) : "—"}</TableCell>
                <TableCell>{s.active ? "Ativa" : "Inativa"}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setEdit(s)} data-testid={`company-edit-${s.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Editar</Button>
                  {!s.is_main && <Button size="icon" variant="ghost" onClick={() => remove(s)} data-testid={`company-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">A quantidade de filiais depende do seu plano. Cada venda fica ligada à unidade escolhida no menu lateral.</p>
        </TabsContent>
      </Tabs>
      <CompanyDialog company={edit} onClose={() => setEdit(null)} onSaved={saved} />
    </>
  );
}
